import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IconAlertTriangle, IconClock, IconDots, IconDownload, IconFilter, IconInbox, IconPlaneDeparture, IconRefresh, IconSearch } from "@tabler/icons";
import PageContainer from "src/components/container/PageContainer";
import PageHeader from "src/components/shared/PageHeader";
import { STATUS, money, travelDay, tripWhen, waitTone, waitingDays, waitingFor } from "./format";
import ActionButton from "src/components/shared/ActionButton";
import { hasModifier, isTestCase, isTyping, lastOpened, readHideTest, saveHideTest, saveQueue, toneColor, useTopBarHeight } from "./workspace";
import BulkCancel from "./BulkCancel";
import BulkAssign from "./BulkAssign";
import BulkNote from "./BulkNote";
import { PaymentChip } from "./workflow";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { listApi } from "./listApi";
import { COLUMN_SORTS, FILTER_KEYS, SORTS, activeFilterCount, casesLink, columnSortLabel, defaultSort, dueEmpty, filterBody, filterChips, readFilters, scopeWords } from "./listParams";
import { approvedAgo, customerLabel, customerWait, dueWords, matchWords, newCopyChips, newCopyWords, ownerLabel } from "./listWords";
import { ActiveFilters, FilterControls, FiltersDialog, OwnerChips } from "./ListFilters";
import { casesCsv } from "./casesCsv";
import { csvName, downloadCsv } from "./csv";

// The views, in the order an agent works through them. A count of null means
// the view needs database update 004.
const VIEWS = [
  { key: "needs_action", label: "Needs action", empty: "Nothing is waiting. Submitted applications appear here." },
  { key: "visa_to_send", label: "Visa to send", empty: "Every approved application has its visa sent.", optional: true },
  { key: "mine", label: "Mine", empty: "No open cases are assigned to you." },
  { key: "unassigned", label: "Unassigned", empty: "Every open case has an owner." },
  { key: "drafts", label: "Drafts", empty: "No customer has an unfinished application." },
  { key: "done", label: "Done", empty: "No application is closed yet." },
  { key: "all", label: "All", empty: "No applications match." },
];

const OPEN = new Set(["pending", "under_review"]);

// Needs action and Visa to send list the paid cases first, then the unpaid
// ones under a divider: neither is filed, nor its visa sent, before payment.
const unpaidDivider = (view) =>
  view === "visa_to_send" ? "Not paid yet · collect the payment before sending the visa" : "Not paid yet · don’t file these until the payment is in";

// The last "Paid" choice, so the sidebar's plain /cases link keeps it.
const PAID_KEY = "safrati.cases.paid";
function readPaid() {
  try {
    const value = window.localStorage.getItem(PAID_KEY);
    return ["paid", "unpaid", "pay_later"].includes(value) ? value : "all";
  } catch {
    return "all";
  }
}
function savePaid(value) {
  try {
    window.localStorage.setItem(PAID_KEY, value);
  } catch {
    /* private window: the choice lasts for this visit */
  }
}

// A column sort's first direction: the largest total first, the rest A to Z
// (waiting: the longest wait first).
const firstDir = (key) => (key === "total" ? "desc" : "asc");

// An older API that doesn't sort this way yet: this page is ordered here,
// and the page says so.
function sortOnPage(rows, sort, dir) {
  const day = (item) => travelDay(item.travel_date) ?? Infinity;
  const time = (item) => new Date(item.submitted_at || 0).getTime();
  const text = (value) => String(value || "").toLowerCase();
  const by = {
    paid_first: (x, y) => Number(!!y.is_paid) - Number(!!x.is_paid),
    trip: (x, y) => day(x) - day(y),
    newest: (x, y) => time(y) - time(x),
    oldest: (x, y) => time(x) - time(y),
    waiting: (x, y) => time(x) - time(y),
    ref: (x, y) => x.id - y.id,
    customer: (x, y) => text(x.customer?.name).localeCompare(text(y.customer?.name)),
    visa: (x, y) => text(x.destination).localeCompare(text(y.destination)),
    status: (x, y) => text(x.status).localeCompare(text(y.status)),
    owner: (x, y) => text(x.assigned?.name || "~").localeCompare(text(y.assigned?.name || "~")),
    total: (x, y) => x.total - y.total,
  }[sort];
  if (!by) return rows;
  const sign = COLUMN_SORTS[sort] && dir === "desc" ? -1 : 1;
  return [...rows].sort((x, y) => sign * by(x, y));
}

// The trip date: "1 Nov" and "in 10 days", red within 14 days while open.
function TripCell({ item }) {
  const trip = tripWhen(travelDay(item.travel_date));
  if (!trip) {
    return (
      <Typography variant="body2" color="text.secondary">
        —<Box component="span" sx={visuallyHidden}>No trip date</Box>
      </Typography>
    );
  }
  const urgent = trip.soon && OPEN.has(item.status);
  return (
    <Box sx={{ whiteSpace: "nowrap", color: urgent ? toneColor("fail") : "text.primary" }}>
      <Typography variant="body2" fontWeight={urgent ? 700 : 600} color="inherit">
        {trip.date}
      </Typography>
      <Typography variant="caption" color={urgent ? "inherit" : "text.secondary"} fontWeight={urgent ? 700 : 400}>
        {trip.when}
      </Typography>
    </Box>
  );
}

TripCell.propTypes = { item: PropTypes.object.isRequired };

// Waiting on the team: the number of days is the text; amber from 3 days and
// red from 7 add an icon of their own shape, so colour is never the only
// signal.
function TeamWait({ item }) {
  const days = waitingDays(item.submitted_at);
  const tone = waitTone(days);
  const text = waitingFor(item.submitted_at) || "—";
  if (tone === "normal") {
    return <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, whiteSpace: "nowrap" }}>{text}</Typography>;
  }
  const late = tone === "late";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        whiteSpace: "nowrap",
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: toneColor(late ? "fail" : "warn"),
        bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : late ? "#FDECEA" : t.palette.warning.light),
      }}
    >
      {late ? <IconAlertTriangle size={14} aria-hidden /> : <IconClock size={14} aria-hidden />}
      {text}
      <Box component="span" sx={visuallyHidden}>
        {late ? ", waiting on the team 7 days or more" : ", waiting on the team 3 days or more"}
      </Box>
    </Box>
  );
}

TeamWait.propTypes = { item: PropTypes.object.isRequired };

// The day promised to the customer (rania-10): "Due 12 Nov · in 3 days",
// red with its own icon once past it ("Late: due 30 Oct, 5 days ago"), the
// waiting days kept beside it.
function DueLine({ item, compact = false }) {
  const due = dueWords(item.due);
  if (!due) return null;
  return (
    <Typography
      variant="caption"
      title={due.why}
      sx={{
        display: compact ? "inline-flex" : "flex",
        alignItems: "center",
        gap: 0.5,
        mt: compact ? 0 : 0.5,
        whiteSpace: "nowrap",
        fontWeight: 700,
        color: due.late ? toneColor("fail") : due.soon ? toneColor("warn") : "text.secondary",
      }}
    >
      {due.late && <IconAlertTriangle size={13} aria-hidden />}
      {compact ? due.short : due.text}
      <Box component="span" sx={visuallyHidden}>
        {` (${due.why})`}
      </Box>
    </Typography>
  );
}

DueLine.propTypes = { item: PropTypes.object.isRequired, compact: PropTypes.bool };

// Who the case waits on, and since when: the team (days since it was
// submitted), the customer (a new copy asked for, a draft; nadia-20), or,
// for an approved case, how long ago it was approved (rania-12).
function WaitCell({ item }) {
  const customer = customerWait(item);
  const approved = approvedAgo(item);
  if (OPEN.has(item.status)) {
    return (
      <Box>
        <TeamWait item={item} />
        <DueLine item={item} />
        {customer && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, whiteSpace: "pre-line", lineHeight: 1.35 }} title={`${customer.text} (${customer.why})`}>
            {/* Two short lines: "Customer ·" / "since 28 Oct". */}
            {customer.short.replace(" · ", " ·\n")}
            <Box component="span" sx={visuallyHidden}>
              {` (${customer.why})`}
            </Box>
          </Typography>
        )}
      </Box>
    );
  }
  if (approved) {
    return (
      <Typography variant="body2" sx={{ fontWeight: approved.days >= 3 ? 700 : 500, color: approved.days >= 3 ? toneColor("warn") : "text.primary" }}>
        {approved.text}
      </Typography>
    );
  }
  if (customer) {
    return (
      <Typography variant="body2" color="text.secondary">
        {customer.text}
        <Box component="span" sx={{ display: "block", fontSize: "0.75rem" }}>
          ({customer.why})
        </Box>
      </Typography>
    );
  }
  // Approved before approvals were dated: the team still has the visa to
  // send (rania-15), when it hasn't gone yet.
  if (item.status === "approved" && item.visa_to_send === true) {
    return (
      <Typography variant="body2" sx={{ fontWeight: 600, color: toneColor("warn") }}>
        Approved, visa not sent
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      —<Box component="span" sx={visuallyHidden}>Not waiting on anyone</Box>
    </Typography>
  );
}

WaitCell.propTypes = { item: PropTypes.object.isRequired };

// The new-copy states are sentences ("New copy asked on 29 Oct 2026,
// waiting for customer"), so they are lines of text, not chips: they wrap
// in their cell instead of widening the table.
function CopyLines({ item }) {
  return newCopyChips(item).map((line) => (
    <Typography
      key={line.key}
      variant="caption"
      sx={{ display: "block", lineHeight: 1.35, mt: 0.5, fontWeight: 600, color: line.tone === "warning" ? toneColor("warn") : "text.secondary" }}
    >
      {line.label}
    </Typography>
  ));
}

function DocumentChips({ item }) {
  if (item.to_review == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  const copies = newCopyChips(item);
  if (!item.to_review && !copies.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Reviewed
      </Typography>
    );
  }
  return (
    <Box>
      {item.to_review > 0 && <Chip size="small" variant="outlined" color="primary" label={`${item.to_review} to review`} aria-label={`${item.to_review === 1 ? "1 document" : `${item.to_review} documents`} to review`} />}
      <CopyLines item={item} />
    </Box>
  );
}

DocumentChips.propTypes = { item: PropTypes.object.isRequired };

const LastOpened = () => (
  <Typography component="span" sx={{ display: "block", fontSize: "0.6875rem", fontWeight: 700, color: "primary.main", whiteSpace: "nowrap" }}>
    Last opened
  </Typography>
);

// Each case is a real link (karim-14): open it in a new tab, copy its link,
// or Tab from one to the next (georges-17).
const caseLinkSx = {
  color: "inherit",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
  "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2, borderRadius: 1 },
};

// A test account's case, shown while a search includes them (karim-04).
const TestChip = () => <Chip size="small" variant="outlined" label="Test" sx={{ ml: 0.75, height: 20, verticalAlign: "middle" }} />;

function CaseRow({ item, selected, last, rowRef, scrollMargin, onOpen, onFocus, selecting, checked, onCheck, showTrip, queue, owners }) {
  const status = STATUS[item.status] ?? { label: item.status, color: "default" };
  const match = matchWords(item.match, item.id);
  const who = customerLabel(item);
  return (
    <TableRow
      ref={rowRef}
      hover
      tabIndex={-1}
      aria-selected={selected}
      onClick={(e) => {
        if (e.target.closest?.("a, button, input, label")) return;
        if (selecting) onCheck(item, !checked);
        else onOpen(item.id);
      }}
      onFocus={onFocus}
      sx={{
        cursor: "pointer",
        scrollMarginTop: scrollMargin,
        scrollMarginBottom: 16,
        "& > td": { py: 1.25, verticalAlign: "middle" },
        "&:focus": { outline: "none" },
        ...(selected && {
          bgcolor: "primary.light",
          "&.MuiTableRow-hover:hover": { bgcolor: "primary.light" },
          "& > td:first-of-type": { boxShadow: (t) => `inset 3px 0 0 ${t.palette.primary.main}` },
        }),
      }}
    >
      {selecting && (
        <TableCell padding="checkbox" sx={{ width: 40, "&&": { pl: 0.5, pr: 0 } }}>
          <Checkbox
            checked={!!checked}
            onChange={(e) => onCheck(item, e.target.checked)}
            inputProps={{ "aria-label": `Select case ${item.id}, ${who?.name || "customer"}` }}
          />
        </TableCell>
      )}
      <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, whiteSpace: "nowrap" }}>
        <Link component={RouterLink} to={`/cases/${item.id}`} state={{ queue }} sx={caseLinkSx} data-case-link={item.id} aria-label={`Case #${item.id}, ${who?.name || "customer"}`}>
          #{item.id}
        </Link>
        {last && <LastOpened />}
      </TableCell>
      {/* Narrower below xl so the table fits a 1366px screen; the text truncates. */}
      <TableCell sx={{ maxWidth: { xs: 150, lg: 170, xl: 220 } }}>
        <Typography fontWeight={600} noWrap title={who?.traveller ? `${who.name} (the first traveller: the account has no name)` : undefined}>
          {who?.name || "—"}
          {item.is_test && <TestChip />}
        </Typography>
        {/* A partner's traveller has no email with us: say who sent it. */}
        {item.partner?.name ? (
          <Typography variant="body2" color="text.secondary" noWrap>
            via {item.partner.name}
          </Typography>
        ) : (
          item.customer.email && (
            <Typography variant="body2" color="text.secondary" noWrap title={item.customer.email}>
              {item.customer.email}
            </Typography>
          )
        )}
        {match && (
          <Typography variant="caption" sx={{ display: "block", color: "primary.main", fontWeight: 600, whiteSpace: "normal" }}>
            {match}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ maxWidth: { xs: 170, lg: 170, xl: 260 } }}>
        <Typography fontWeight={600} noWrap title={item.destination || undefined}>
          {item.destination || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap title={item.product || undefined}>
          {item.product} · {item.travellers === 1 ? "1 traveller" : `${item.travellers} travellers`}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" color={status.color} label={status.label} />
          {item.status !== "incomplete" && <PaymentChip caseData={item} />}
        </Stack>
      </TableCell>
      <TableCell sx={{ maxWidth: { lg: 170, xl: 220 } }}>
        <DocumentChips item={item} />
      </TableCell>
      <TableCell>
        <WaitCell item={item} />
      </TableCell>
      {showTrip && (
        <TableCell>
          <TripCell item={item} />
        </TableCell>
      )}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        {item.assigned ? (
          <Tooltip title={item.assigned.name}>
            <Typography variant="body2" fontWeight={600} aria-label={`Owner: ${item.assigned.name}`}>
              {ownerLabel(item.assigned.name, owners)}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nobody
          </Typography>
        )}
      </TableCell>
      <TableCell align="right" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
        {money(item.total)}
      </TableCell>
    </TableRow>
  );
}

CaseRow.propTypes = {
  item: PropTypes.object.isRequired,
  selecting: PropTypes.bool,
  checked: PropTypes.bool,
  onCheck: PropTypes.func,
  selected: PropTypes.bool,
  last: PropTypes.bool,
  rowRef: PropTypes.func,
  scrollMargin: PropTypes.number,
  onOpen: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  showTrip: PropTypes.bool,
  queue: PropTypes.object,
  owners: PropTypes.array,
};

// On a phone, and at 200 % zoom, each case is a card: the table would not
// fit. Two lines (karim-05), so five or six fit a screen: who and the case
// number; then the status, the money and what is due. It keeps the money
// (paid or not, and the total; georges-01) and is a real link, except while
// selecting. A search's match adds a line.
function CaseCard({ item, selected, last, rowRef, scrollMargin, onFocus, selecting, checked, onCheck, queue, owners }) {
  const status = STATUS[item.status] ?? { label: item.status, color: "default" };
  const trip = item.travel_date ? tripWhen(travelDay(item.travel_date)) : null;
  const match = matchWords(item.match, item.id);
  const who = customerLabel(item);
  const due = dueWords(item.due);
  const linkProps = selecting ? {} : { component: RouterLink, to: `/cases/${item.id}`, state: { queue } };
  return (
    <Card
      ref={rowRef}
      variant="outlined"
      {...linkProps}
      onClick={selecting ? () => onCheck(item, !checked) : undefined}
      onFocus={onFocus}
      aria-current={last ? "true" : undefined}
      aria-label={selecting ? undefined : `Case #${item.id}, ${who?.name || "customer"}, ${item.destination || ""}`}
      data-case-link={selecting ? undefined : item.id}
      sx={{
        px: 1.5,
        py: 1.25,
        display: "block",
        color: "text.primary",
        textDecoration: "none",
        cursor: "pointer",
        scrollMarginTop: scrollMargin,
        "&:focus": { outline: "none" },
        "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: "2px" },
        ...((selected || checked) && { bgcolor: "primary.light", borderColor: "primary.main" }),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {selecting && (
          <Checkbox
            checked={!!checked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onCheck(item, e.target.checked)}
            inputProps={{ "aria-label": `Select case ${item.id}, ${who?.name || "customer"}` }}
            sx={{ ml: -1, my: -1 }}
          />
        )}
        <Typography noWrap sx={{ flex: 1, minWidth: 0 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {who?.name || "—"}
          </Box>
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {" · "}
            {item.destination || item.product}
            {item.partner?.name ? ` · via ${item.partner.name}` : ""}
          </Box>
        </Typography>
        {item.is_test && <TestChip />}
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, whiteSpace: "nowrap" }}>
          #{item.id}
        </Typography>
      </Stack>
      {/* The row wraps: the lateness pill moves to a line of its own,
          on the right, rather than being cut at the card's edge (karim-01). */}
      <Stack direction="row" spacing={0.75} mt={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0, rowGap: 0.5 }}>
        <Chip size="small" color={status.color} label={status.label} />
        {item.status !== "incomplete" && <PaymentChip caseData={item} />}
        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, whiteSpace: "nowrap" }}>
          {money(item.total)}
        </Typography>
        {item.to_review > 0 && (
          <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 600, whiteSpace: "nowrap" }}>
            {item.to_review} to review
          </Typography>
        )}
        <Box sx={{ ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.75, maxWidth: "100%" }}>
          {due ? (
            <DueLine item={item} compact />
          ) : OPEN.has(item.status) ? (
            <TeamWait item={item} />
          ) : trip ? (
            <Typography variant="body2" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap", color: "text.secondary" }}>
              <IconPlaneDeparture size={14} aria-hidden />
              {trip.date}
            </Typography>
          ) : null}
          {last && <LastOpened />}
        </Box>
      </Stack>
      {match && (
        <Typography variant="caption" sx={{ display: "block", color: "primary.main", fontWeight: 600, mt: 0.5 }}>
          {match}
        </Typography>
      )}
      {item.assigned && owners && (
        <Box component="span" sx={visuallyHidden}>
          {` Owner: ${item.assigned.name}.`}
        </Box>
      )}
    </Card>
  );
}

CaseCard.propTypes = CaseRow.propTypes;

// One tile, a link to the list it counts (rania-07). `extra` is a second
// link inside it (the 7+ days of the waiting tile), above the tile's own.
function Tile({ label, value, note, tone, loading, to, extra }) {
  return (
    <Card
      variant="outlined"
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        minWidth: 0,
        position: "relative",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Link
          component={RouterLink}
          to={to}
          underline="hover"
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "text.secondary",
            "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "20px" },
            "&:focus-visible": { outline: "none" },
            "&:focus-visible::after": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
          }}
        >
          {label}
        </Link>
        {loading ? (
          <Skeleton width={40} height={32} />
        ) : (
          <Typography sx={{ fontSize: "1.5rem", lineHeight: 1.2, fontWeight: 700, color: tone ? toneColor(tone) : "text.primary" }}>{value}</Typography>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
        {loading ? <Skeleton width="70%" /> : note}
        {!loading && extra && (
          <>
            {" · "}
            <Link component={RouterLink} to={extra.to} sx={{ position: "relative", zIndex: 1, fontWeight: 600 }}>
              {extra.label}
            </Link>
          </>
        )}
      </Typography>
    </Card>
  );
}

Tile.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  note: PropTypes.node,
  tone: PropTypes.string,
  loading: PropTypes.bool,
  to: PropTypes.string.isRequired,
  extra: PropTypes.object,
};

// Four numbers to start the day with, each a link to its list. With
// data.stats they cover every open case under the filters; an older API
// leaves them counted from this page, and the tile says so. On a phone they
// are one line, so the cases start on the first screen (nadia-40).
function SummaryStrip({ data, viewLabel, search, scope, link, compact }) {
  const loading = !data;
  const stats = data?.stats;
  const rows = data?.cases || [];
  const open = rows.filter((item) => OPEN.has(item.status));
  const waits = open.map((item) => waitingDays(item.submitted_at) ?? 0);
  const old = stats ? stats.waiting_3d : waits.filter((d) => d >= 3).length;
  const late = stats ? stats.waiting_7d : waits.filter((d) => d >= 7).length;
  const docsKnown = data?.workspace_ready !== false;
  const toReview = stats ? stats.documents_to_review : rows.reduce((sum, item) => sum + (item.to_review || 0), 0);
  const copies = stats ? stats.new_copies_requested : rows.reduce((sum, item) => sum + (item.needs_new || 0), 0);
  const asked = stats?.new_copies_asked;
  const partial = data && data.total > rows.length;
  const note = stats ? scope : partial ? `On this page only (${rows.length} of ${data.total})` : search ? "In these search results" : `In ${viewLabel}`;
  // The two states, in the case screen's words (nadia-15).
  const notSent = asked == null ? 0 : Math.max(copies - asked, 0);
  const copiesNote =
    asked == null
      ? note
      : [notSent ? newCopyWords(notSent, "needed") : null, asked ? `${asked} asked, waiting for customer` : null].filter(Boolean).join(" · ") || note;
  const tiles = [
    { key: "needs", label: "Needs action", value: data?.counts?.needs_action ?? 0, note, to: link({ view: "needs_action", waiting: "", docs: "", due: "" }), short: `${data?.counts?.needs_action ?? 0} need action` },
    {
      key: "waiting",
      label: "Waiting 3+ days",
      value: old,
      tone: late ? "fail" : old ? "warn" : undefined,
      note: late ? `${late} of them 7+ days` : note,
      to: link({ view: "needs_action", waiting: 3, docs: "", due: "" }),
      extra: late ? { label: "Show them", to: link({ view: "needs_action", waiting: 7, docs: "", due: "" }) } : null,
      short: `${old} waiting 3+ days`,
    },
    {
      key: "docs",
      label: "Documents to review",
      value: docsKnown ? toReview : "—",
      // Documents, not cases (rania-15, georges-16): "in 349 cases".
      note: docsKnown ? (stats?.documents_to_review_cases != null ? `${toReview === 1 ? "1 document" : `${toReview} documents`} in ${stats.documents_to_review_cases === 1 ? "1 case" : `${stats.documents_to_review_cases} cases`}` : note) : "After database update 004",
      to: link({ view: "needs_action", docs: "to_review", waiting: "", due: "" }),
      short: `${docsKnown ? toReview : "—"} documents to review`,
    },
    {
      key: "copies",
      label: "New copies asked for",
      value: docsKnown ? copies : "—",
      tone: docsKnown && copies ? "warn" : undefined,
      note: docsKnown ? (copies ? copiesNote : "Documents the team asked the customer to send again") : "After database update 004",
      to: link({ view: "needs_action", docs: "new_copies", waiting: "", due: "" }),
      short: `${docsKnown ? copies : "—"} new copies asked for`,
    },
  ];
  // Late means past the day promised to the customer (rania-10), when the
  // server knows the days: the waiting days stay in their own tile.
  if (stats && stats.past_due != null) {
    tiles.splice(2, 0, {
      key: "due",
      label: "Past the promised day",
      value: stats.past_due,
      tone: stats.past_due ? "fail" : undefined,
      note: stats.due_this_week ? `${stats.due_this_week} more due within 7 days` : "Nothing else due within 7 days",
      to: link({ view: "needs_action", due: "past", waiting: "", docs: "" }),
      extra: stats.due_this_week ? { label: "Show them", to: link({ view: "needs_action", due: "week", waiting: "", docs: "" }) } : null,
      short: `${stats.past_due} past the promised day`,
    });
  }
  // On a phone: small links, so the cases start near the top (karim-05).
  // They wrap: a row that scrolled sideways ran off the edge at 200 % zoom
  // and on a 360 px phone with larger text (georges-08, karim-13).
  if (compact) {
    return (
      <Box component="nav" aria-label="Summary" sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
        {loading
          ? [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" width={120} height={32} sx={{ flexShrink: 0 }} />)
          : tiles.map((tile) => (
              <Chip
                key={tile.key}
                component={RouterLink}
                to={tile.to}
                clickable
                variant="outlined"
                label={tile.short}
                sx={{ maxWidth: "100%", height: 36, fontWeight: 700, color: tile.tone ? toneColor(tile.tone) : "primary.main" }}
              />
            ))}
      </Box>
    );
  }
  return (
    <Box
      component="section"
      aria-label="Summary"
      sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: `repeat(${tiles.length}, minmax(0, 1fr))` }, gap: 1.5, mb: 2 }}
    >
      {tiles.map((tile) => (
        <Tile key={tile.key} {...tile} loading={loading} />
      ))}
    </Box>
  );
}

SummaryStrip.propTypes = {
  data: PropTypes.object,
  viewLabel: PropTypes.string,
  search: PropTypes.string,
  scope: PropTypes.string,
  link: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

const Key = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: "inherit",
      fontSize: "0.75rem",
      fontWeight: 600,
      px: 0.6,
      mx: 0.25,
      border: 1,
      borderColor: "divider",
      borderBottomWidth: 2,
      borderRadius: 0.75,
      bgcolor: "background.paper",
      color: "text.secondary",
    }}
  >
    {children}
  </Box>
);

Key.propTypes = { children: PropTypes.node };

const linkButtonSx = { p: 0, border: 0, bgcolor: "transparent", color: "primary.main", font: "inherit", fontWeight: 600, cursor: "pointer", textDecoration: "underline" };

const dividerSx = {
  py: 1,
  px: 1.5,
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: 0.2,
  color: toneColor("warn"),
  bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : t.palette.warning.light),
  borderBottom: 0,
};

// The column headers that sort (rania-16, nadia-03).
const HEADERS = [
  { key: "ref", label: "Ref", width: 96 },
  { key: "customer", label: "Customer" },
  { key: "visa", label: "Visa" },
  { key: "status", label: "Status" },
  { key: "documents", label: "Documents", sorts: false },
  { key: "waiting", label: "Waiting" },
  { key: "trip", label: "Trip", tripOnly: true },
  { key: "owner", label: "Owner" },
  { key: "total", label: "Total", align: "right" },
];

const Cases = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const phone = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const topBar = useTopBarHeight();
  const [params, setParams] = useSearchParams();
  const search = params.get("q") || "";
  // A search looks through every tab unless one is picked (karim-04): the
  // tabs then count the matches ("2 in Drafts, 1 in Done").
  const view = VIEWS.some((v) => v.key === params.get("view")) ? params.get("view") : search ? "all" : "needs_action";
  const page = Math.max(Number(params.get("page")) || 0, 0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [typed, setTyped] = useState(search);
  const [state, setState] = useState({ loading: true, failed: false, data: null });
  const [selected, setSelected] = useState(-1);
  const [lastId] = useState(lastOpened);
  const [searchFocused, setSearchFocused] = useState(false);
  // Paid filter and the test-account switch, both in the address (and
  // remembered): an address without them (the sidebar link) uses the last
  // choice. The switch is the same one Home, Key numbers and Money use.
  const paid = ["paid", "unpaid", "pay_later", "all"].includes(params.get("paid")) ? params.get("paid") : readPaid();
  const sortParam = params.get("sort");
  const sort = SORTS.some((x) => x.key === sortParam) || COLUMN_SORTS[sortParam] ? sortParam : defaultSort(view);
  const dir = params.get("dir") === "desc" ? "desc" : "asc";
  const hideTest = params.has("tests") ? params.get("tests") !== "1" : readHideTest();
  const filters = useMemo(() => readFilters(params), [params]);
  const filterKey = JSON.stringify(filters);
  // Selecting rows for a bulk change.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [options, setOptions] = useState(null);
  const [exporting, setExporting] = useState(false);
  // Every case under the filters, once "Select all N that match" is pressed
  // (rania-11): the bulk actions then work on them, not just this page.
  const [allMatching, setAllMatching] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [assignToMe, setAssignToMe] = useState(false);
  const [toolsAnchor, setToolsAnchor] = useState(null);
  // The tab a search started from, to go back to once it is cleared.
  const viewBeforeSearch = useRef(null);
  const showTrip = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const privileges = getLocalStorage(dataLocalStorage.privileges) || {};
  const canEdit = Number(privileges["23"]?.access_edit) === 1;
  const me = getLocalStorage(dataLocalStorage.userinfo)?.id ?? null;
  const latest = useRef(0);
  const loadedOnce = useRef(false);
  const retried = useRef(false);
  const rowRefs = useRef([]);
  const searchRef = useRef(null);
  const restored = useRef(false);

  // The view, search, page and filters live in the address, so back and
  // reload keep them, and a link opens the same list.
  const update = useCallback(
    (changes) => {
      const next = new URLSearchParams(params);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === "" || value == null || (key === "page" && value === 0)) next.delete(key);
        else next.set(key, String(value));
      });
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  // A filter change starts again on page 1.
  const setFilters = useCallback(
    (changes, extra = {}) => {
      if (extra.savePaid) savePaid(extra.savePaid);
      update({ ...changes, page: 0 });
    },
    [update],
  );

  // A search includes test accounts, as Ctrl K does, each marked Test
  // (karim-04): someone looking for a person finds them either way.
  const hidingTests = hideTest && !search;
  const body = useMemo(
    () => ({ view, search, page, rowsPerPage, paid, sort, ...(COLUMN_SORTS[sort] ? { dir } : {}), hide_test: hidingTests, ...filterBody(JSON.parse(filterKey)) }),
    [view, search, page, rowsPerPage, paid, sort, dir, hidingTests, filterKey],
  );
  // A selection belongs to one list: a new list starts it again.
  useEffect(() => {
    setAllMatching(null);
  }, [body]);

  const load = useCallback(() => {
    const ticket = ++latest.current;
    setState((s) => ({ ...s, loading: true, failed: false }));
    listApi
      .list({ ...body, options: !options }, dispatch)
      .then((response) => {
        if (ticket !== latest.current) return;
        loadedOnce.current = true;
        if (response?.data?.options) setOptions(response.data.options);
        setState({ loading: false, failed: false, data: response?.data });
      })
      .catch(() => {
        if (ticket !== latest.current) return;
        // The first load can lose a race with the sign-in check (karim-15):
        // try once more, quietly, before saying it failed.
        if (!loadedOnce.current && !retried.current) {
          retried.current = true;
          setTimeout(() => {
            if (ticket === latest.current) load();
          }, 1200);
          return;
        }
        setState((s) => ({ ...s, loading: false, failed: true }));
      });
    // options: asked for once; reloading for it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, dispatch]);

  // The team, for "Assign to…".
  useEffect(() => {
    if (!selecting || agents.length) return;
    listApi
      .agents(dispatch)
      .then((response) => setAgents(response?.data?.agents || []))
      .catch(() => setAgents([]));
  }, [selecting, agents.length, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // Search as the agent types, a beat after they stop. A new search looks
  // through every tab; clearing it goes back to the tab it started from.
  useEffect(() => {
    const next = typed.trim();
    if (next === search) return undefined;
    const timer = setTimeout(() => {
      if (next && !search) {
        viewBeforeSearch.current = params.get("view") || "";
        update({ q: next, page: 0, view: "" });
      } else if (!next && search) {
        update({ q: "", page: 0, view: viewBeforeSearch.current || "" });
        viewBeforeSearch.current = null;
      } else update({ q: next, page: 0 });
    }, 300);
    return () => clearTimeout(timer);
  }, [typed, search, update, params]);
  // Typed but not answered yet: the list says it is working (rania-13).
  const searching = typed.trim() !== search || (state.loading && !!search);

  const data = state.data;
  // The server applies the paid and test-account filters when it knows them
  // (it then echoes data.filters), and the others when it echoes
  // data.applied. Until then they apply to this page only, or not at all,
  // and the page says so.
  const serverFiltered = !!data?.filters;
  const serverKnowsFilters = !!data?.applied;
  const sortedByServer = data?.filters?.sort === sort;
  const cases = useMemo(() => {
    let rows = data?.cases || [];
    if (!serverFiltered) {
      if (hideTest) rows = rows.filter((item) => !isTestCase(item));
      if (paid === "paid") rows = rows.filter((item) => item.is_paid);
      if (paid === "unpaid") rows = rows.filter((item) => !item.is_paid);
      if (paid === "pay_later") rows = rows.filter((item) => !item.is_paid && item.pay_later);
    }
    // While a new order loads, the old page stays as it was (dimmed): it is
    // never half-sorted here and called sorted (nadia-07).
    if (data && !sortedByServer && !state.loading) rows = sortOnPage(rows, sort, dir);
    if (view === "visa_to_send") rows = rows.map((item) => ({ ...item, visa_to_send: true }));
    return rows;
  }, [data, serverFiltered, hideTest, paid, sort, dir, sortedByServer, state.loading, view]);
  const sortedOnPage = !!data && !state.loading && !sortedByServer && sort !== "oldest";
  const filtersIgnored = !!data && !serverKnowsFilters && activeFilterCount(filters) > 0;
  // Where the unpaid cases start, for the "Not paid yet" divider.
  const firstUnpaid = sort === "paid_first" && paid === "all" ? cases.findIndex((item) => !item.is_paid && item.status !== "incomplete") : -1;
  const dividerAt = firstUnpaid > 0 || (firstUnpaid === 0 && page === 0) ? firstUnpaid : -1;
  // The server counts the open test cases it leaves out (test_open).
  const testHidden = hidingTests ? (serverFiltered ? Number(data?.test_open ?? data?.test_hidden) || 0 : (data?.cases || []).filter(isTestCase).length) : 0;
  const pickedCases = (allMatching || cases).filter((item) => picked.includes(item.id));
  // Drafts cannot change status (the customer has not submitted), and a
  // cancelled case has nothing to cancel.
  // Approved cases (the visa may be with the customer) are never cancelled in bulk.
  const cancellable = pickedCases.filter((item) => !["incomplete", "cancelled", "approved"].includes(item.status));
  const approvedPicked = pickedCases.filter((item) => item.status === "approved").length;
  const stopSelecting = () => {
    setSelecting(false);
    setPicked([]);
    setAllMatching(null);
  };
  // "Select all 347 that match" (rania-11): every case under the filters,
  // up to the export's 2,000.
  const selectAllMatching = async () => {
    setLoadingAll(true);
    try {
      const response = await listApi.exportAll(body, dispatch);
      const rows = response?.data?.cases || [];
      setAllMatching(rows);
      setPicked(rows.map((item) => item.id));
      if (rows.length < (Number(response?.data?.total) || rows.length)) toast.warn(`Selected the first ${rows.length} of ${response.data.total}. Narrow the filters to reach the rest.`);
    } catch {
      toast.error("The cases couldn’t all be selected. Nothing changed. Try again.");
    } finally {
      setLoadingAll(false);
    }
  };
  // The selection as a CSV file (karim-20).
  const exportPicked = () => {
    downloadCsv(csvName("Cases", "selected"), casesCsv(pickedCases));
    toast.success(`Exported ${pickedCases.length === 1 ? "1 case" : `${pickedCases.length} cases`}.`);
  };
  const counts = data?.counts || {};
  // "Visa to send" appears once the API counts it. On a phone Mine comes
  // second, so it is on the first screen of the tab strip (karim-05).
  const PHONE_ORDER = ["needs_action", "mine", "unassigned", "visa_to_send", "drafts", "done", "all"];
  const views = VIEWS.filter((v) => !v.optional || counts[v.key] != null || v.key === view).sort((x, y) =>
    phone ? PHONE_ORDER.indexOf(x.key) - PHONE_ORDER.indexOf(y.key) : 0,
  );
  const viewInfo = VIEWS.find((v) => v.key === view);
  // Counts on every tab or on none: a count on some tabs only reads as if the
  // others were empty (QA F33). While searching, the tabs count the matches.
  // They show once the API counts every view.
  const tabCounts = search ? data?.search_matches || {} : counts;
  const allCounted = views.every((v) => typeof tabCounts[v.key] === "number");
  // A search with no match here, but some in other views (data.search_matches).
  const matches = search && data?.search_matches;
  const elsewhere = matches
    ? VIEWS.filter((v) => v.key !== view && v.key !== "all" && matches[v.key] > 0).map((v) => ({ key: v.key, label: v.label, count: matches[v.key] }))
    : [];
  const testMatches = matches && hidingTests ? Number(matches.test_accounts) || 0 : 0;
  const owners = data?.owners || null;
  const ownerNames = (owners || []).map((o) => o.name).filter(Boolean);
  const chips = filterChips(filters, { paid, names: { destinations: options?.destinations, visaTypes: options?.visa_types, owners }, me });
  // An empty list under "Due within 7 days" or "Past the promised day" says so and offers the next list.
  const dueNone = !search && filters.due ? dueEmpty(filters.due, data?.stats, { others: chips.length > 1 }) : null;
  // The Paid choice comes back from the last visit when the address has
  // none: said at the top, with Clear (rania-12, nadia-20).
  const paidKept = !params.has("paid") && paid !== "all" ? chips.find((chip) => chip.key === "paid") : null;
  const scope = scopeWords(chips, search);

  // The order on screen, for Next / Previous on the case page.
  const queue = useMemo(
    () => ({
      ids: cases.map((item) => item.id),
      label: search ? `${viewInfo.label}, search “${search}”` : viewInfo.label,
      search: params.toString() ? `?${params.toString()}` : "",
      partial: !!data && data.total > cases.length,
    }),
    [cases, data, search, viewInfo.label, params],
  );

  useEffect(() => {
    if (data) saveQueue(queue);
  }, [data, queue]);

  // Coming back: select the case opened last and bring it into view, once.
  useEffect(() => {
    if (!data) return;
    const index = cases.findIndex((item) => item.id === lastId);
    setSelected(index);
    if (index >= 0 && !restored.current) {
      restored.current = true;
      requestAnimationFrame(() => rowRefs.current[index]?.scrollIntoView({ block: "nearest" }));
    }
  }, [data, cases, lastId]);

  const open = useCallback((id) => navigate(`/cases/${id}`, { state: { queue } }), [navigate, queue]);

  // Moving puts the keyboard on the case's own link, so Enter opens it and
  // Tab goes on to the next case.
  const select = useCallback(
    (index) => {
      if (!cases.length) return;
      const next = Math.min(Math.max(index, 0), cases.length - 1);
      setSelected(next);
      const element = rowRefs.current[next];
      if (element) {
        const target = element.matches?.("[data-case-link]") ? element : element.querySelector?.("[data-case-link]") || element;
        target.focus({ preventScroll: true });
        element.scrollIntoView({ block: "nearest" });
      }
    },
    [cases.length],
  );

  // j / k and the arrows move, Enter opens, "/" searches (Gmail's keys).
  useEffect(() => {
    const onKey = (event) => {
      if (event.defaultPrevented || hasModifier(event)) return;
      const typing = isTyping(event);
      if (event.key === "/" && !typing) {
        event.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (typing) return;
      const onRow = rowRefs.current.some((row) => row && (row === event.target || row.contains?.(event.target)));
      const free = onRow || event.target === document.body;
      if (event.key === "j" || (onRow && event.key === "ArrowDown")) {
        event.preventDefault();
        select(selected < 0 ? 0 : selected + 1);
      } else if (event.key === "k" || (onRow && event.key === "ArrowUp")) {
        event.preventDefault();
        select(selected < 0 ? 0 : selected - 1);
      } else if (event.key === "Enter" && free && !onRow && selected >= 0 && cases[selected]) {
        event.preventDefault();
        open(cases[selected].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [select, selected, cases, open]);

  const rowProps = (item, index) => ({
    item,
    selected: index === selected,
    last: item.id === lastId,
    rowRef: (node) => {
      rowRefs.current[index] = node;
    },
    scrollMargin: topBar + (phone ? 12 : 52),
    onOpen: open,
    onFocus: () => setSelected(index),
    selecting,
    checked: picked.includes(item.id),
    onCheck: (row, on) => setPicked((ids) => (on ? [...new Set([...ids, row.id])] : ids.filter((id) => id !== row.id))),
    showTrip,
    queue,
    owners: ownerNames,
  });

  const showTests = (show) => {
    saveHideTest(!show);
    update({ tests: show ? "1" : "0", page: 0 });
  };

  // A link to this list with some settings changed (the tiles).
  const link = (changes) =>
    casesLink({
      view,
      paid,
      tests: params.get("tests") || undefined,
      ...Object.fromEntries(FILTER_KEYS.map((key) => [key, params.get(key) || ""])),
      q: search,
      ...changes,
    });

  const sortBy = (key) => {
    if (sort === key) update({ dir: dir === "asc" ? "desc" : "asc", page: 0 });
    else update({ sort: key, dir: firstDir(key), page: 0 });
  };
  // A header's sort shows in the Order menu too ("Owner, A to Z").
  const customSort = COLUMN_SORTS[sort] && !(SORTS.some((x) => x.key === sort) && dir === "asc");
  const sortOptions = customSort ? [...SORTS.filter((x) => x.key !== sort), { key: sort, label: columnSortLabel(sort, dir) }] : SORTS;

  // Every case under these filters, as a CSV file (rania-37).
  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await listApi.exportAll(body, dispatch);
      const rows = response?.data?.cases || [];
      const total = Number(response?.data?.total) || rows.length;
      downloadCsv(csvName("Cases", viewInfo.label, search ? "search" : ""), casesCsv(rows));
      if (rows.length < total) toast.warn(`Exported the first ${rows.length} of ${total} cases. Narrow the filters to export the rest.`);
      else toast.success(`Exported ${rows.length === 1 ? "1 case" : `${rows.length} cases`} from ${viewInfo.label}${chips.length || search ? ", with these filters" : ""}.`);
    } catch {
      toast.error("The export didn’t work. Nothing was downloaded. Try again.");
    } finally {
      setExporting(false);
    }
  };

  // Where a search found cases (karim-04): "2 in Needs action · 1 in
  // Drafts", each narrowing the list to that tab; searching one tab, the
  // others' matches.
  const matchLine = (elsewhere.length > 0 || testMatches > 0) && (
    <Stack direction="row" alignItems="center" columnGap={0.75} rowGap={0.75} flexWrap="wrap" role="status" sx={{ px: 1, pt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {view === "all" ? (cases.length ? "Found in:" : "No match.") : cases.length === 0 ? `No match in ${viewInfo.label}. Found in:` : "Also found in:"}
      </Typography>
      {elsewhere.map((v) => (
        <Chip
          key={v.key}
          size="small"
          clickable
          variant="outlined"
          color="primary"
          label={`${v.count} in ${v.label}`}
          aria-label={`Show the ${v.count} ${v.count === 1 ? "match" : "matches"} in ${v.label}`}
          onClick={() => update({ view: v.key, page: 0 })}
        />
      ))}
      {view !== "all" && elsewhere.length > 0 && (
        <Chip size="small" clickable variant="outlined" label="Every tab" onClick={() => update({ view: "all", page: 0 })} />
      )}
      {testMatches > 0 && (
        <Typography variant="body2" color="text.secondary">
          {testMatches} {testMatches === 1 ? "match" : "matches"} in test accounts ·{" "}
          <Box component="button" type="button" onClick={() => showTests(true)} sx={linkButtonSx}>
            Show them
          </Box>
        </Typography>
      )}
    </Stack>
  );

  const hasRows = !!data && cases.length > 0;
  const controlProps = {
    view,
    paid,
    filters,
    sort,
    sortOptions,
    onSort: (value) => update({ sort: value === defaultSort(view) ? "" : value, dir: "", page: 0 }),
    options,
    owners: owners || [],
    me,
    set: setFilters,
    // Before the first answer the owner menu shows; an API without owners
    // (before database update 004) hides it.
    ownersReady: !data || owners != null,
  };
  const filterCount = activeFilterCount(filters) + (paid !== "all" ? 1 : 0);
  const testLine = ((testHidden > 0 && !testMatches) || (!serverFiltered && paid !== "all") || sortedOnPage || (search && hideTest)) && (
    <Typography variant="body2" color="text.secondary" role="status">
      {search && hideTest && "A search includes test accounts, as Ctrl K does: they are marked Test."}
      {testHidden > 0 && !testMatches && (
        <>
          {serverFiltered
            ? testHidden === 1
              ? "1 open test case hidden"
              : `${testHidden} open test cases hidden`
            : testHidden === 1
              ? "1 test case hidden on this page"
              : `${testHidden} test cases hidden on this page`}{" "}
          ·{" "}
          <Box component="button" type="button" onClick={() => showTests(true)} sx={linkButtonSx}>
            Show them
          </Box>
        </>
      )}
      {!serverFiltered && paid !== "all" && `${testHidden > 0 && !testMatches ? " · " : ""}The paid filter applies to this page only.`}
      {sortedOnPage && `${(testHidden > 0 && !testMatches) || (!serverFiltered && paid !== "all") ? " · " : ""}Sorted on this page only.`}
    </Typography>
  );
  const selectButton = canEdit && hasRows && !selecting && (
    <ActionButton variant="text" onClick={() => setSelecting(true)}>
      Select
    </ActionButton>
  );
  // On a phone Export and Select sit behind one ⋯ button (karim-05).
  const phoneTools = hasRows && (
    <>
      <ActionButton
        variant="text"
        startIcon={<IconDots size={18} />}
        aria-haspopup="menu"
        aria-expanded={!!toolsAnchor}
        aria-label="More: export, select"
        onClick={(e) => setToolsAnchor(e.currentTarget)}
      >
        More
      </ActionButton>
      <Menu anchorEl={toolsAnchor} open={!!toolsAnchor} onClose={() => setToolsAnchor(null)}>
        <MenuItem
          onClick={() => {
            setToolsAnchor(null);
            exportCsv();
          }}
          disabled={exporting}
          sx={{ minHeight: 44 }}
        >
          {exporting ? "Exporting…" : "Export these cases (CSV)"}
        </MenuItem>
        {canEdit && !selecting && (
          <MenuItem
            onClick={() => {
              setToolsAnchor(null);
              setSelecting(true);
            }}
            sx={{ minHeight: 44 }}
          >
            Select cases
          </MenuItem>
        )}
      </Menu>
    </>
  );
  const exportButton = hasRows && (
    <ActionButton variant="text" startIcon={<IconDownload size={16} />} busyLabel="Exporting…" busy={exporting} onClick={exportCsv} aria-label="Export CSV">
      {phone ? "Export" : "Export CSV"}
    </ActionButton>
  );

  return (
    <PageContainer title="Cases" description="Visa applications to work on">
      <PageHeader
        title="Cases"
        subtitle={phone ? undefined : "Applications to check, file and follow up."}
        sx={{ mb: phone ? 1 : 2 }}
        actions={
          <TextField
            size="small"
            value={typed}
            inputRef={searchRef}
            onChange={(e) => setTyped(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.currentTarget.querySelector("input")?.blur();
                if (selected >= 0) select(selected);
              } else if (e.key === "ArrowDown" && cases.length) {
                e.preventDefault();
                select(selected < 0 ? 0 : selected);
              }
            }}
            placeholder={phone ? "Name, #, passport, phone…" : "Any traveller, #, passport, phone or destination"}
            sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 400 }, "& .MuiInputBase-root": { minHeight: 40 } }}
            inputProps={{ "aria-label": "Search cases by any traveller’s name, case or request number, passport, phone or destination", "aria-keyshortcuts": "/" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch size={18} />
                </InputAdornment>
              ),
              // A spinner in the box while a search works (rania-13).
              endAdornment: searching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} aria-label="Searching" />
                </InputAdornment>
              ) : !phone && !typed && !searchFocused ? (
                <InputAdornment position="end" aria-hidden>
                  <Key>/</Key>
                </InputAdornment>
              ) : undefined,
            }}
          />
        }
      />

      {data && data.workspace_ready === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Owners, notes and document reviews switch on after database update 004. Until then you can open and read every case.
        </Alert>
      )}

      {!(state.failed && !data) && <SummaryStrip data={data} viewLabel={viewInfo.label} search={search} scope={scope} link={link} compact={phone} />}

      <Card variant="outlined" sx={{ overflow: "visible", p: { xs: 1, sm: "15px" } }}>
        <Tabs
          value={view}
          onChange={(_, key) => update({ view: key === "needs_action" && !search ? "" : key, sort: "", dir: "", page: 0 })}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ px: 1, borderBottom: 1, borderColor: "divider", "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 48 } }}
        >
          {views.map((v) => (
            <Tab
              key={v.key}
              value={v.key}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{v.label}</span>
                  {allCounted && <Chip size="small" label={tabCounts[v.key]} />}
                </Stack>
              }
            />
          ))}
        </Tabs>

        {/* What to show, and the tools: on a phone one Filters button. */}
        {phone ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, pt: 1.5 }}>
            <ActionButton startIcon={<IconFilter size={16} />} onClick={() => setFiltersOpen(true)} aria-haspopup="dialog">
              Filters{filterCount ? ` (${filterCount})` : ""}
            </ActionButton>
            <Box sx={{ flex: 1 }} />
            {phoneTools}
          </Stack>
        ) : (
          <>
            <Stack direction="row" columnGap={1} rowGap={1} alignItems="center" flexWrap="wrap" sx={{ px: 1, pt: 1.5 }}>
              <FilterControls {...controlProps} />
            </Stack>
            <Stack direction="row" columnGap={1.5} rowGap={0.5} alignItems="center" flexWrap="wrap" sx={{ px: 1, pt: 0.75 }}>
              <FormControlLabel control={<Switch checked={hideTest} onChange={(e) => showTests(!e.target.checked)} />} label="Hide test accounts" sx={{ mr: 0, ml: 0 }} />
              {testLine}
              {owners && owners.length > 0 && ["needs_action", "unassigned", "mine", "all"].includes(view) && (
                <OwnerChips owners={owners} me={me} selected={filters.owner} onPick={(value) => setFilters({ owner: value })} />
              )}
              <Box sx={{ flex: 1 }} />
              {exportButton}
              {selectButton}
            </Stack>
          </>
        )}
        {phone && testLine && <Box sx={{ px: 1, pt: 1 }}>{testLine}</Box>}
        {paidKept && (
          <Alert
            severity="info"
            sx={{ mx: 1, mt: 1, py: 0, alignItems: "center" }}
            action={
              <ActionButton variant="text" onClick={() => setFilters({ paid: "all" }, { savePaid: "all" })}>
                Clear
              </ActionButton>
            }
          >
            Showing “{paidKept.label}” only: kept from last time.
          </Alert>
        )}
        <ActiveFilters
          chips={chips}
          onClear={(keys) => {
            if (keys.includes("paid")) savePaid("all");
            setFilters(Object.fromEntries(keys.map((key) => [key, key === "paid" ? "all" : ""])));
          }}
          onClearAll={() => {
            savePaid("all");
            setFilters({ ...Object.fromEntries(FILTER_KEYS.map((key) => [key, ""])), paid: "all" });
          }}
        />
        {filtersIgnored && (
          <Alert severity="warning" sx={{ mx: 1, mt: 1 }}>
            This server doesn’t filter by these yet: the list shows every case in {viewInfo.label}. It will once the server is updated.
          </Alert>
        )}

        {selecting && (
          <Box role="region" aria-label="Selection" sx={{ mx: 1, mt: 1, px: 1.5, py: 0.75, borderRadius: "20px", bgcolor: "primary.light" }}>
            <Stack direction="row" alignItems="center" columnGap={1} rowGap={1} flexWrap="wrap">
              <Checkbox
                size="small"
                checked={cases.length > 0 && cases.every((item) => picked.includes(item.id))}
                indeterminate={pickedCases.length > 0 && !cases.every((item) => picked.includes(item.id))}
                onChange={(e) => {
                  setAllMatching(null);
                  setPicked(e.target.checked ? cases.map((item) => item.id) : []);
                }}
                inputProps={{ "aria-label": "Select every case on this page" }}
              />
              <Typography fontWeight={600} aria-live="polite">
                {pickedCases.length ? `${pickedCases.length} selected` : "Select cases"}
                {pickedCases.length > cancellable.length && (
                  <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                    {" "}
                    · {approvedPicked > 0 ? `${approvedPicked} approved` : ""}
                    {approvedPicked > 0 && pickedCases.length - cancellable.length > approvedPicked ? ", " : ""}
                    {pickedCases.length - cancellable.length > approvedPicked ? `${pickedCases.length - cancellable.length - approvedPicked} draft or already cancelled` : ""}
                    , can’t be cancelled
                  </Typography>
                )}
              </Typography>
              {/* Closing the selection is "Stop selecting", never "Cancel":
                  Cancel is only for applications, in red at the far end,
                  and it always asks first (rania-09, nadia-19). */}
              <ActionButton variant="text" onClick={stopSelecting}>
                Stop selecting
              </ActionButton>
              {me != null && (
                <ActionButton disabled={!pickedCases.length} onClick={() => setAssignToMe(true)}>
                  Assign to me
                </ActionButton>
              )}
              <ActionButton disabled={!pickedCases.length} onClick={() => setAssignOpen(true)}>
                Assign to…
              </ActionButton>
              <ActionButton disabled={!pickedCases.length} onClick={() => setNoteOpen(true)}>
                Add a team note
              </ActionButton>
              <ActionButton disabled={!pickedCases.length} startIcon={<IconDownload size={16} />} onClick={exportPicked}>
                Export these
              </ActionButton>
              <Box sx={{ flex: 1, minWidth: { sm: 24 } }} />
              <ActionButton
                variant="outlined"
                disabled={!cancellable.length}
                onClick={() => setBulkOpen(true)}
                sx={(t) => ({
                  color: toneColor("fail")(t),
                  borderColor: toneColor("fail")(t),
                  "&:hover": { borderColor: toneColor("fail")(t), bgcolor: t.palette.mode === "dark" ? "rgba(253,162,155,0.12)" : "#FDECEA" },
                  "&.Mui-disabled": { color: toneColor("fail")(t), borderColor: toneColor("fail")(t) },
                })}
              >
                Cancel the applications…
              </ActionButton>
            </Stack>
            {/* After the whole page: every case that matches (rania-11). */}
            {data && data.total > cases.length && cases.length > 0 && cases.every((item) => picked.includes(item.id)) && (
              <Typography variant="body2" sx={{ pl: 1, pb: 0.5 }} aria-live="polite">
                {allMatching ? (
                  <>
                    All {allMatching.length} cases that match are selected ·{" "}
                    <Box component="button" type="button" onClick={() => { setAllMatching(null); setPicked(cases.map((item) => item.id)); }} sx={linkButtonSx}>
                      Only this page
                    </Box>
                  </>
                ) : (
                  <>
                    All {cases.length} on this page are selected ·{" "}
                    <Box component="button" type="button" onClick={selectAllMatching} disabled={loadingAll} sx={linkButtonSx}>
                      {loadingAll ? "Selecting…" : `Select all ${Math.min(data.total, 2000)} that match`}
                    </Box>
                  </>
                )}
              </Typography>
            )}
          </Box>
        )}

        {matchLine}

        {/* Working: a thin bar and, for a search, its words (nadia-02, rania-13). */}
        {data && (state.loading || searching) && (
          <Box sx={{ px: 1, pt: 1 }} role="status">
            <LinearProgress sx={{ height: 3, borderRadius: 2 }} aria-label={searching ? `Searching for “${typed.trim()}”` : "Loading cases"} />
            {searching && typed.trim() && (
              <Typography variant="caption" color="text.secondary">
                Searching for “{typed.trim()}”…
              </Typography>
            )}
          </Box>
        )}

        {state.failed && (
          <Alert
            severity="error"
            sx={{ m: 2, alignItems: "center" }}
            action={
              <ActionButton onClick={load} startIcon={<IconRefresh size={16} />}>
                Try again
              </ActionButton>
            }
          >
            {data ? "The cases did not refresh. This is the list as it was last loaded." : "The cases did not load. Check your connection and try again."}
          </Alert>
        )}

        {!state.failed && state.loading && !data && (
          <Box p={2} aria-busy="true" aria-label="Loading cases">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={56} sx={{ mb: 1 }} />
            ))}
          </Box>
        )}

        {data && cases.length === 0 && (
          <Stack alignItems="center" spacing={1.5} py={6} px={2} textAlign="center">
            <Box sx={{ color: "text.secondary" }}>
              <IconInbox size={32} />
            </Box>
            <Typography fontWeight={600}>
              {search
                ? chips.length
                  ? "No case matches this search with these filters."
                  : "No case matches this search."
                : dueNone
                ? dueNone.text
                : chips.length
                ? "No case matches these filters."
                : viewInfo.empty}
            </Typography>
            {/* Nothing due: the next useful list, and the way out (rania-18). */}
            {dueNone && (
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                {dueNone.next && (
                  <ActionButton variant="contained" component={RouterLink} to={link(dueNone.next.changes)}>
                    {dueNone.next.label}
                  </ActionButton>
                )}
                <ActionButton onClick={() => setFilters(dueNone.clear.changes)}>{dueNone.clear.label}</ActionButton>
              </Stack>
            )}
            {search && chips.length > 0 && <Typography variant="body2" color="text.secondary">Filters on: {chips.map((chip) => chip.label).join(" · ")}</Typography>}
            <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
              {search && chips.length > 0 && (
                <ActionButton
                  variant="contained"
                  onClick={() => {
                    savePaid("all");
                    setFilters({ ...Object.fromEntries(FILTER_KEYS.map((key) => [key, ""])), paid: "all" });
                  }}
                >
                  Clear the filters
                </ActionButton>
              )}
              {search && (
                <ActionButton
                  onClick={() => {
                    setTyped("");
                    update({ q: "", page: 0, view: viewBeforeSearch.current || "" });
                    viewBeforeSearch.current = null;
                  }}
                >
                  Clear the search
                </ActionButton>
              )}
            </Stack>
          </Stack>
        )}

        {hasRows && (
          <Box sx={{ opacity: state.loading || searching ? 0.6 : 1, transition: "opacity 120ms" }} aria-busy={state.loading || searching || undefined}>
            {phone ? (
              <Stack spacing={1} p={1}>
                {cases.map((item, index) => [
                  index === dividerAt && (
                    <Typography key="unpaid-divider" sx={{ ...dividerSx, pt: index ? 1.5 : 0.5 }}>
                      {unpaidDivider(view)}
                    </Typography>
                  ),
                  <CaseCard key={item.id} {...rowProps(item, index)} />,
                ])}
              </Stack>
            ) : (
              <Table
                size="small"
                aria-label={`${viewInfo.label} cases`}
                // Tighter cells below xl, so every column fits a 1366px screen (nadia-16).
                // Selecting adds a column, so the cells tighten a little more.
                sx={{ "& td, & th": { px: { lg: selecting ? 0.75 : 1, xl: 2 } }, "& td:first-of-type, & th:first-of-type": { pl: { lg: 1.5 } } }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& > th": {
                        position: "sticky",
                        top: topBar,
                        zIndex: 2,
                        bgcolor: "background.paper",
                        color: "text.secondary",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        py: 1.25,
                        whiteSpace: "nowrap",
                        boxShadow: (t) => `inset 0 -1px 0 ${t.palette.divider}`,
                        borderBottom: 0,
                      },
                    }}
                  >
                    {selecting && (
                      <TableCell padding="checkbox" sx={{ width: 40, "&&": { pl: 0.5, pr: 0 } }}>
                        <Box component="span" sx={visuallyHidden}>
                          Select
                        </Box>
                      </TableCell>
                    )}
                    {HEADERS.filter((h) => !h.tripOnly || showTrip).map((h) => {
                      const active = sort === h.key || (h.key === "waiting" && sort === "oldest") || (h.key === "trip" && sort === "trip");
                      const shownDir = sort === "oldest" ? "asc" : dir;
                      return (
                        <TableCell
                          key={h.key}
                          align={h.align || "left"}
                          sx={{ width: h.width }}
                          aria-sort={h.sorts === false ? undefined : active ? (shownDir === "desc" ? "descending" : "ascending") : "none"}
                        >
                          {h.sorts === false ? (
                            h.label
                          ) : (
                            <TableSortLabel active={active} direction={active ? shownDir : firstDir(h.key)} onClick={() => sortBy(h.key)}>
                              {h.label}
                              <Box component="span" sx={visuallyHidden}>
                                {active ? `, sorted ${COLUMN_SORTS[h.key][shownDir]}` : ", sort by this column"}
                              </Box>
                            </TableSortLabel>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cases.map((item, index) => [
                    index === dividerAt && (
                      <TableRow key="unpaid-divider">
                        <TableCell colSpan={(selecting ? 1 : 0) + (showTrip ? 9 : 8)} sx={dividerSx}>
                          {unpaidDivider(view)}
                        </TableCell>
                      </TableRow>
                    ),
                    <CaseRow key={item.id} {...rowProps(item, index)} />,
                  ])}
                </TableBody>
              </Table>
            )}
            <Stack direction={{ xs: "column", md: "row" }} alignItems={{ md: "center" }} justifyContent="space-between" sx={{ px: 1 }}>
              {!phone ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  <Key>Tab</Key> or <Key>↑</Key>
                  <Key>↓</Key> move · <Key>Enter</Key> open · <Key>/</Key> search · a column’s name sorts by it
                </Typography>
              ) : (
                <span />
              )}
              <TablePagination
                component="div"
                count={data.total}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Rows per page"
              onPageChange={(_, next) => update({ page: next })}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                update({ page: 0 });
              }}
            />
            </Stack>
          </Box>
        )}
      </Card>
      {phone && (
        <FiltersDialog
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          count={filterCount}
          hideTest={hideTest}
          onHideTest={(on) => showTests(!on)}
          {...controlProps}
        />
      )}
      {(assignOpen || assignToMe) && (
        <BulkAssign
          cases={pickedCases}
          agents={agents}
          owners={owners || []}
          initialOwner={assignToMe && me != null ? String(me) : ""}
          onClose={() => {
            setAssignOpen(false);
            setAssignToMe(false);
          }}
          onDone={() => {
            setAssignOpen(false);
            setAssignToMe(false);
            stopSelecting();
            load();
          }}
        />
      )}
      {noteOpen && (
        <BulkNote
          cases={pickedCases}
          onClose={() => setNoteOpen(false)}
          onDone={() => {
            setNoteOpen(false);
            stopSelecting();
          }}
        />
      )}
      {bulkOpen && (
        <BulkCancel
          cases={cancellable}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false);
            stopSelecting();
            load();
          }}
          onUndone={load}
        />
      )}
    </PageContainer>
  );
};

export default Cases;
